import React, { useState, useMemo } from 'react';
import {
  Card,
  Button,
  Table,
  Space,
  Tag,
  Input,
  Modal,
  Row,
  Col,
  Typography,
  Statistic,
  Spin,
  message,
  Badge,
  Upload,
  Progress,
  Select,
} from 'antd';
import type { UploadProps } from 'antd/es/upload';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/services/api.service';
import { postExcelImportWithRetry } from '@/utils/excelImportUpload';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  InboxOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  StopOutlined,
  DownloadOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { AedDirhamSign } from '@/components/currency';
import { useCurrency } from '@/contexts/CurrencyContext';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  groupItemDependencies,
  itemDeleteFailureMessage,
  parseItemDeletionBlocked,
  type ItemDeletionBlocked,
} from '@/utils/itemDeletion';

const { Title, Text } = Typography;
const { Search } = Input;

function unwrap<T>(res: unknown): T {
  const data = (res as any)?.data ?? res;
  return (data?.data ?? data) as T;
}

const ITEMS_QUERY_KEY = ['items'];

interface ItemApi {
  id: string;
  item_code: string;
  item_name: string;
  item_description?: string;
  rack_number?: string | null;
  category?: { id?: string; category_name?: string };
  item_type: string;
  base_uom: string;
  reorder_level?: number;
  minimum_stock_level?: number;
  maximum_stock_level?: number;
  standard_cost?: number;
  standard_selling_price?: number;
  lead_time_days?: number;
  is_active?: boolean;
  is_critical?: boolean;
  profile_grade?: string | null;
  profile_heat_no?: string | null;
  profile_dimension?: string | null;
  profile_length?: string | null;
}

interface StockRow {
  id?: string;
  item_id?: string;
  item_code?: string;
  available_qty?: string | number;
  current_stock?: number;
}

export const ItemsPage: React.FC = () => {
  const { formatCurrency } = useCurrency();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const itemsListPath = location.pathname.startsWith('/platform')
    ? '/platform/foundation/items'
    : '/scm/inventory/items';
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebouncedValue(searchText.trim(), 300);
  const [criticalityFilter, setCriticalityFilter] = useState<'all' | 'critical' | 'non-critical'>('all');
  const [isExcelBusy, setIsExcelBusy] = useState(false);
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [pageSize, setPageSize] = useState(20);

  const {
    data: itemsRaw = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: [...ITEMS_QUERY_KEY, debouncedSearch || '', criticalityFilter],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (debouncedSearch) params.search = debouncedSearch;
      if (criticalityFilter === 'critical') params.is_critical = 'true';
      if (criticalityFilter === 'non-critical') params.is_critical = 'false';
      const res = await api.get('/inventory/items', {
        params: Object.keys(params).length ? params : undefined,
      });
      const list = unwrap<ItemApi[]>(res);
      return Array.isArray(list) ? list : [];
    },
  });

  const { data: stockRows = [] } = useQuery({
    queryKey: ['items', 'stock'],
    queryFn: async () => {
      try {
        const res = await api.get('/inventory/items/stock');
        const d = unwrap<StockRow[]>(res);
        return Array.isArray(d) ? d : [];
      } catch {
        return [];
      }
    },
  });

  const { data: lowStockList = [] } = useQuery({
    queryKey: ['items', 'low-stock'],
    queryFn: async () => {
      try {
        const res = await api.get('/inventory/items/low-stock');
        const d = unwrap<ItemApi[]>(res);
        return Array.isArray(d) ? d : [];
      } catch {
        return [];
      }
    },
  });

  const stockByItemId = useMemo(() => {
    const m = new Map<string, number>();
    for (const row of stockRows) {
      const id = (row as StockRow).id ?? (row as StockRow).item_id;
      if (!id) continue;
      const qty = Number((row as StockRow).available_qty ?? (row as StockRow).current_stock ?? 0);
      m.set(id, qty);
    }
    return m;
  }, [stockRows]);

  const columns: ColumnsType<ItemApi> = [
    {
      title: 'Item code',
      dataIndex: 'item_code',
      key: 'item_code',
      width: 150,
      sorter: (a, b) => (a.item_code || '').localeCompare(b.item_code || ''),
      render: (code: string, record) => (
        <Space size={6} wrap>
          <Button
            type="link"
            style={{ padding: 0, height: 'auto' }}
            onClick={() => navigate(`${itemsListPath}/${record.id}`)}
          >
            {code}
          </Button>
          {record.is_critical ? <Tag color="red">Critical</Tag> : null}
        </Space>
      ),
    },
    {
      title: 'Item details',
      key: 'details',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.item_name}</Text>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            {record.category?.category_name || '—'}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'item_type',
      key: 'item_type',
      width: 100,
      render: (type: string) => {
        const colors: { [key: string]: string } = {
          Stock: 'blue',
          'Non-Stock': 'orange',
          Service: 'purple',
          Subcontract: 'cyan',
        };
        const label = type === 'Subcontract' ? 'Subcontract / Job-work' : type;
        return <Tag color={colors[type] || 'default'}>{label}</Tag>;
      },
    },
    {
      title: 'Criticality',
      key: 'is_critical',
      width: 120,
      render: (_, record) =>
        record.is_critical ? (
          <Tag color="red">Critical</Tag>
        ) : (
          <Tag color="default">Non-critical</Tag>
        ),
    },
    {
      title: 'Rack',
      dataIndex: 'rack_number',
      key: 'rack_number',
      width: 100,
      ellipsis: true,
      render: (v: string) => v || '—',
    },
    {
      title: 'Grade',
      dataIndex: 'profile_grade',
      key: 'profile_grade',
      width: 110,
      ellipsis: true,
      render: (v: string) => v || '—',
    },
    {
      title: 'Heat No',
      dataIndex: 'profile_heat_no',
      key: 'profile_heat_no',
      width: 110,
      ellipsis: true,
      render: (v: string) => v || '—',
    },
    {
      title: 'Current stock',
      key: 'stock',
      width: 120,
      align: 'right',
      render: (_, record) => {
        if (['Service', 'Non-Stock', 'Subcontract'].includes(record.item_type)) {
          return <Text type="secondary">N/A</Text>;
        }
        const qty = stockByItemId.get(record.id);
        if (qty === undefined) {
          return <Text type="secondary">—</Text>;
        }
        const reorder = Number(record.reorder_level) || 0;
        const isLow = qty <= reorder;
        const stockSeverelyLow = reorder > 0 && qty < reorder * 0.5;

        let icon = <CheckCircleOutlined style={{ color: '#52c41a' }} />;
        let color = '#52c41a';
        if (stockSeverelyLow) {
          icon = <StopOutlined style={{ color: '#f5222d' }} />;
          color = '#f5222d';
        } else if (isLow) {
          icon = <WarningOutlined style={{ color: '#faad14' }} />;
          color = '#faad14';
        }

        return (
          <Space>
            {icon}
            <Text strong style={{ color }}>
              {qty} {record.base_uom}
            </Text>
          </Space>
        );
      },
    },
    {
      title: 'Reorder level',
      key: 'reorderLevel',
      width: 110,
      align: 'right',
      render: (_, record) => {
        if (['Service', 'Non-Stock', 'Subcontract'].includes(record.item_type)) return '—';
        const level = Number(record.reorder_level) || 0;
        return `${level} ${record.base_uom}`;
      },
    },
    {
      title: 'Unit cost',
      dataIndex: 'standard_cost',
      key: 'standard_cost',
      width: 110,
      align: 'right',
      render: (v: number) => <Text>{formatCurrency(Number(v ?? 0))}</Text>,
      sorter: (a, b) => Number(a.standard_cost) - Number(b.standard_cost),
    },
    {
      title: 'Selling price',
      dataIndex: 'standard_selling_price',
      key: 'standard_selling_price',
      width: 110,
      align: 'right',
      render: (v: number) => (
        <Text strong>{formatCurrency(Number(v ?? 0))}</Text>
      ),
      sorter: (a, b) => Number(a.standard_selling_price) - Number(b.standard_selling_price),
    },
    {
      title: 'Status',
      key: 'status',
      width: 110,
      render: (_, record) =>
        record.is_active !== false ? (
          <Tag color="success">Active</Tag>
        ) : (
          <Tag>Inactive</Tag>
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`${itemsListPath}/${record.id}`)}
          >
            View
          </Button>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => navigate(`${itemsListPath}/${record.id}/edit`)}
          />
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          />
        </Space>
      ),
    },
  ];

  const refreshItemQueries = async () => {
    await queryClient.invalidateQueries({ queryKey: ITEMS_QUERY_KEY });
    await queryClient.invalidateQueries({ queryKey: ['items', 'stock'] });
    await queryClient.invalidateQueries({ queryKey: ['items', 'low-stock'] });
  };

  const showDeletionBlocked = (item: ItemApi, blocked: ItemDeletionBlocked) => {
    const content = (
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <Text>{blocked.message}</Text>
        {groupItemDependencies(blocked.dependencies).map((group) => (
          <div key={group.title}>
            <Text strong>{group.title}</Text>
            <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
              {group.items.map((d) => (
                <li key={d.key}>
                  {d.label}: {d.count}
                  {d.references.length > 0 &&
                    ` (${d.references.join(', ')}${d.count > d.references.length ? ', …' : ''})`}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </Space>
    );
    if (item.is_active === false) {
      Modal.warning({ title: 'Item cannot be deleted', content, okText: 'Close', width: 560 });
      return;
    }
    Modal.confirm({
      title: 'Item cannot be deleted',
      content,
      width: 560,
      okText: 'Mark Inactive',
      cancelText: 'Close',
      async onOk() {
        try {
          await api.put(`/inventory/items/${item.id}`, { is_active: false });
          message.success(`${item.item_code} marked Inactive`);
          await refreshItemQueries();
        } catch {
          message.error('The item could not be marked Inactive. Please try again.');
        }
      },
    });
  };

  const handleDelete = (item: ItemApi) => {
    Modal.confirm({
      title: 'Delete item',
      content: `Delete "${item.item_name}"?`,
      okText: 'Delete',
      okType: 'danger',
      async onOk() {
        try {
          await api.delete(`/inventory/items/${item.id}`);
          message.success('Item deleted');
          await refreshItemQueries();
        } catch (error) {
          const blocked = parseItemDeletionBlocked(error);
          if (blocked) showDeletionBlocked(item, blocked);
          else message.error(itemDeleteFailureMessage(error));
        }
      },
    });
  };

  const triggerBlobDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadTemplate = async () => {
    setIsExcelBusy(true);
    try {
      const res = await api.getClient().get('/inventory/items/excel/template', {
        responseType: 'blob',
      });
      triggerBlobDownload(
        new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        'items-import-template.xlsx'
      );
      message.success('Template downloaded');
    } catch (err: any) {
      message.error(err?.response?.data?.message || err?.message || 'Download failed');
    } finally {
      setIsExcelBusy(false);
    }
  };

  const handleExportExcel = async () => {
    setIsExcelBusy(true);
    try {
      const res = await api.getClient().get('/inventory/items/excel/export', {
        responseType: 'blob',
      });
      const dateStr = new Date().toISOString().slice(0, 10);
      triggerBlobDownload(
        new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        `items-${dateStr}.xlsx`
      );
      message.success('Export downloaded');
    } catch (err: any) {
      message.error(err?.response?.data?.message || err?.message || 'Export failed');
    } finally {
      setIsExcelBusy(false);
    }
  };

  const handleImportExcel: UploadProps['customRequest'] = async (options) => {
    const { file, onError, onSuccess } = options;
    const formData = new FormData();
    formData.append('file', file as File);
    setIsExcelBusy(true);
    setUploadPct(0);
    try {
      const res = await postExcelImportWithRetry(
        api.getClient(),
        '/inventory/items/excel/import',
        formData,
        {
          timeout: 300000,
          onUploadProgress: (p) => setUploadPct(p),
        }
      );
      const body = res.data as {
        success?: boolean;
        message?: string;
        data?: { created: number; updated: number; errors?: string[] };
      };
      if (body?.success !== false) {
        const d = body?.data;
        const errList = d?.errors?.length ? ` (${d.errors.length} row message(s))` : '';
        message.success((body?.message || 'Import completed') + errList);
        if (d?.errors?.length) {
          Modal.warning({
            title: 'Import notes',
            width: 560,
            content: (
              <div style={{ maxHeight: 280, overflow: 'auto' }}>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {d.errors!.slice(0, 50).map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
                {d.errors!.length > 50 ? <Text type="secondary">…and more</Text> : null}
              </div>
            ),
          });
        }
        await queryClient.invalidateQueries({ queryKey: ITEMS_QUERY_KEY });
        await queryClient.invalidateQueries({ queryKey: ['items', 'stock'] });
        await queryClient.invalidateQueries({ queryKey: ['items', 'low-stock'] });
        onSuccess?.(body, file as any);
      } else {
        throw new Error(body?.message || 'Import failed');
      }
    } catch (err: any) {
      message.error(err?.response?.data?.message || err?.message || 'Import failed');
      onError?.(err as Error);
    } finally {
      setIsExcelBusy(false);
      setUploadPct(null);
    }
  };

  const totalStockItems = itemsRaw.filter((i) => i.item_type === 'Stock').length;
  const activeItems = itemsRaw.filter((i) => i.is_active !== false).length;
  const lowStockItems = lowStockList.length;
  const stockValue = useMemo(() => {
    let sum = 0;
    for (const i of itemsRaw) {
      if (i.item_type !== 'Stock') continue;
      const q = stockByItemId.get(i.id) ?? 0;
      sum += q * (Number(i.standard_cost) || 0);
    }
    return sum;
  }, [itemsRaw, stockByItemId]);

  if (isError) {
    return (
      <div>
        <Title level={2}>Items &amp; Products</Title>
        <Card>
          <Text type="danger">Failed to load items. {(error as Error)?.message}</Text>
          <Button type="primary" onClick={() => refetch()} style={{ marginTop: 8 }}>
            Retry
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>Items &amp; Products</Title>
        <Text type="secondary">Manage product catalog, stock levels, pricing, and Excel import/export</Text>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total stock items"
              value={totalStockItems}
              prefix={<InboxOutlined />}
              valueStyle={{ color: '#145A54' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="Active items" value={activeItems} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Badge count={lowStockItems} offset={[10, 0]}>
              <Statistic
                title="Low stock alert"
                value={lowStockItems}
                prefix={<WarningOutlined />}
                valueStyle={{ color: lowStockItems > 0 ? '#faad14' : '#52c41a' }}
              />
            </Badge>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Stock value (cost)"
              value={stockValue}
              prefix={<AedDirhamSign />}
              valueStyle={{ color: '#145A54' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        {uploadPct !== null && (
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary">Uploading import file…</Text>
            <Progress percent={uploadPct} size="small" />
          </div>
        )}
        <div
          style={{
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '8px',
          }}
        >
          <Space wrap>
            <Search
              placeholder="Search code, name, Rack, Grade, Heat No…"
              allowClear
              style={{ width: 320 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <Select
              value={criticalityFilter}
              onChange={(v) => setCriticalityFilter(v)}
              style={{ width: 160 }}
              options={[
                { value: 'all', label: 'All criticality' },
                { value: 'critical', label: 'Critical' },
                { value: 'non-critical', label: 'Non-critical' },
              ]}
            />
            <Button icon={<DownloadOutlined />} onClick={handleDownloadTemplate} disabled={isExcelBusy}>
              Template
            </Button>
            <Button icon={<DownloadOutlined />} onClick={handleExportExcel} disabled={isExcelBusy}>
              Export
            </Button>
            <Upload
              accept=".xlsx,.xls"
              showUploadList={false}
              customRequest={handleImportExcel}
              disabled={isExcelBusy}
            >
              <Button icon={<UploadOutlined />} loading={isExcelBusy}>
                Import
              </Button>
            </Upload>
          </Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate(`${itemsListPath}/new`)}
          >
            Add item
          </Button>
        </div>

        <Spin spinning={isLoading}>
          <Table
            columns={columns}
            dataSource={itemsRaw}
            rowKey="id"
            rowClassName={(record) => (record.is_critical ? 'scm-critical-item-row' : '')}
            pagination={{
              pageSize,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              onShowSizeChange: (_current, size) => setPageSize(size),
              showTotal: (total) => `Total ${total} items`,
            }}
            size="small"
          />
        </Spin>
      </Card>
    </div>
  );
};
